import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext'
import {
    LexicalTypeaheadMenuPlugin,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
    $getSelection,
    $isRangeSelection,
    $isRootOrShadowRoot,
    COMMAND_PRIORITY_CRITICAL,
    SELECTION_CHANGE_COMMAND,
    TextNode,
} from 'lexical'
import {InternalLinkOption} from "./option"
import {SlashMenu} from "./menu"
import {lexicalInsertLink} from "../../actions/insert-link"
import {lexicalInsertMentionTrigger} from "../../actions/insert-mention-trigger"
import {lexicalInsertEmojiTrigger} from "../../actions/insert-emoji-trigger"
import {EntitySelectorEntityType} from "../../../entity-selector/selector"
import {EntitySelectorSettings} from "../../editor"
import {$isHeadingNode} from "@lexical/rich-text"
import editor_headingOptions from "./options/heading-options"
import {blockTypeToBlockName} from "../toolbar/toolbar"
import {$findMatchingParent, $getNearestNodeOfType, mergeRegister} from "@lexical/utils"
import {$isListNode, ListNode} from "@lexical/list"
import editor_alignOptions from "./options/align-options"
import {$isParentElementRTL} from "@lexical/selection"
import editor_insertOptions from "./options/insert-options"
import editor_listOptions from "./options/list-options"
import {PopoverMenuItemType} from "../../../popover-menu/popover-menu/const"
import editor_linkOptions from "./options/link-options";

type Props = {
    entitySelectorSettings  : EntitySelectorSettings
    projectId?              : ID
}

const debug = false
// const debug = "SlashMenuPlugin"

export default function SlashMenuPlugin(props: Props): JSX.Element {
    const [editor]                          = useLexicalComposerContext()
    const [queryString, setQueryString]     = useState<string | null>(null)
    const [activeEditor, setActiveEditor]   = useState(editor)
    const [blockType, setBlockType]         = useState<keyof typeof blockTypeToBlockName>('custom-paragraph')
    const [isRTL, setIsRTL]                 = useState(false)

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                        const parent = e.getParent()
                        return parent !== null && $isRootOrShadowRoot(parent);
                    });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow()
            }

            const elementKey = element.getKey()
            const elementDOM = activeEditor.getElementByKey(elementKey)

            setIsRTL($isParentElementRTL(selection))

            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(
                        anchorNode,
                        ListNode,
                    );
                    const type = parentList
                        ? parentList.getListType()
                        : element.getListType();
                    setBlockType(type);
                } else {
                    const type = $isHeadingNode(element)
                        ? element.getTag()
                        : element.getType();
                    if (type in blockTypeToBlockName) {
                        setBlockType(type as keyof typeof blockTypeToBlockName);
                    }
                }
            }
        }
    }, [activeEditor])

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                $updateToolbar()
                setActiveEditor(newEditor)
                return false
            },
            COMMAND_PRIORITY_CRITICAL,
        );
    }, [editor, $updateToolbar])

    useEffect(() => {
        return mergeRegister(
            activeEditor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    $updateToolbar()
                })
            }),
        )
    }, [$updateToolbar, activeEditor, editor])

    const checkForTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
        minLength: 0,
    })

    const options = useMemo(() => {
        const baseOptions = [
            ...editor_headingOptions(activeEditor, blockType),
            ...editor_alignOptions(activeEditor, isRTL),
            ...editor_listOptions(activeEditor, blockType),
            ...editor_linkOptions(activeEditor, props.entitySelectorSettings),
            ...editor_insertOptions(activeEditor),
        ]

        return queryString
            ? [
                ...baseOptions.filter((option) => {
                    return new RegExp(queryString, 'gi').exec(option.label) ||
                    option.keywords != null ? option.keywords.some((keyword) => new RegExp(queryString, 'gi').exec(keyword)) : false
                }),
            ]
            : baseOptions
    }, [editor, queryString])

    const onSelectOption = useCallback(
        (
            selectedOption  : InternalLinkOption,
            nodeToRemove    : TextNode | null,
            closeMenu       : () => void,
            matchingString  : string,
        ) => {
            switch (selectedOption.type) {
                case PopoverMenuItemType.SUBMENU:
                    /*do nothing*/
                    break

                default:
                    editor.update(() => {
                        if (nodeToRemove) {
                            nodeToRemove.remove()
                        }
                        selectedOption.action && selectedOption.action(matchingString)
                        closeMenu()
                    })
            }
        },
        [editor],
    )

    debug && console.log(debug, 'render', props.entitySelectorSettings)

    return (
        <LexicalTypeaheadMenuPlugin<InternalLinkOption> onQueryChange   = {setQueryString}
                                                        onSelectOption  = {onSelectOption}
                                                        triggerFn       = {checkForTriggerMatch}
                                                        options         = {options}
                                                        menuRenderFn    = {SlashMenu}/>
    )
}
